import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import Head from 'next/head';
import getConfig from 'next/config';
import { logError } from 'lib/datadog';
import { GridStack } from 'components/GridStack';
// eslint-disable-next-line import/no-unresolved
import { Gamma } from '@nbcnews/gamma';
import { stub as $t } from '@nbcnews/analytics-framework';
import classNames from 'classnames';
import BrowserDetection from 'lib/BrowserDetection';
import { isVideoLive } from 'lib/videoUtils';
import { getFeatureConfigForBrand } from 'lib/getFeatureStatus';
import { VerticalContext, BreakpointContext } from 'lib/ContextTypes';
import { USE_MPX_VIDEO_PARAMS, ENABLE_RETINA } from 'lib/brandFeatures';
// eslint-disable-next-line import/no-unresolved
import { useConsentStatus } from '@nbcnews/gpp-utilities';
import { JW_PLAYER_LIBRARY_URL } from 'lib/constants';
import { useShouldRestrictVideoAdsByGeoLocation } from 'lib/Hooks/useShouldRestrictVideoAdsByGeoLocation';
import styles from 'components/Gamma/VideoPlayer/styles.module.scss';
import { useVideoControl } from '../VideoPlayer/useVideoControl';
import { selectVideoData } from '../VideoPlayer/gammaUtils/selectVideoData';
import { useAdConfigurator } from '../VideoPlayer/gammaUtils/getAdvertisingConfig';

import { ControlOverlay } from './ControlOverlay';
import { Slate } from './Slate';

const {
  publicRuntimeConfig: { JW_PLAYER_LIBRARY_KEY },
} = getConfig();

/**
 *
 */
const emptyFunction = () => {};

/**
 * @typedef {import('@nbcnews/gamma').GammaProps} GammaProps
 * @typedef {GammaProps["playlistItem"]} PlaylistItem
 * @typedef {import('../VideoPlayer/gammaUtils/usePlaylistAdapter').Playlist} Playlist
 * @typedef {import('../VideoPlayer/gammaUtils/selectVideoData').VideoData} VideoData
 * @typedef { VideoData & {
 *   associatedVideoPlaylist?: Playlist;
 * }} Video
 */

/**
 * @param {object} props
 * @param {boolean} [props.autoPlay]
 * @param {boolean} [props.useEmbeddedAds]
 * @param {React.RefObject<HTMLElement>} [props.primaryFocusElementRef]
 * @param {boolean} [props.continuousPlay]
 * @param {boolean} [props.isTouchSupported]
 * @param {() => void} [props.onVideoEnd]
 * @param {() => void} [props.onVideoPlay]
 * @param {VideoData} props.video
 * @param {GammaProps['config']} props.config
 * @param {boolean} [props.shouldPlayAd]
 * @param {() => void} [props.handlePrevious]
 * @param {() => void} [props.handleNext]
 * @param {() => void} [props.handleClose]
 * @param {() => void} [props.handeAdPlayed]
 * @param {() => void} [props.handleVideoStart]
 * @param {number} [props.playlistIndex]
 * @param {string} [props.playlistId]
 * @returns {JSX.Element}
 */
export function GammaVerticalVideoPlayer({
  autoPlay,
  useEmbeddedAds = false,
  primaryFocusElementRef,
  continuousPlay,
  isTouchSupported,
  onVideoEnd = emptyFunction,
  onVideoPlay = emptyFunction,
  video,
  config,
  shouldPlayAd,
  playlistIndex,
  playlistId = '',
  handlePrevious = emptyFunction,
  handleNext = emptyFunction,
  handleClose = emptyFunction,
  handeAdPlayed = emptyFunction,
  handleVideoStart = emptyFunction,
}) {
  const { data: gppStatus } = useConsentStatus();
  const vertical = React.useContext(VerticalContext);
  const { isM, isL, isXL } = React.useContext(BreakpointContext);
  const useMpxParams = getFeatureConfigForBrand(USE_MPX_VIDEO_PARAMS, vertical);
  const enableRetina = getFeatureConfigForBrand(ENABLE_RETINA, vertical);
  const isLive = isVideoLive(video);
  const [playReason, setPlayReason] = useState('auto');
  const playCount = React.useRef(0);
  const jwRef = React.useRef(null);

  const shouldRestrictVideoAds = useShouldRestrictVideoAdsByGeoLocation();
  const playAd = !shouldRestrictVideoAds && shouldPlayAd;

  const {
    title,
    isExpired,
    playlistItem,
    publisherName,
  } = selectVideoData(video, {
    useMpxParams,
    isLive,
    vertical,
    isEmbedded: useEmbeddedAds,
    gppStatus,
    breakpoints: { isM, isL, isXL },
    enableRetina,
  });

  // Check if captions are available for this video
  const hasCaptions = Boolean(
    video?.closedCaptioning?.webvtt || video?.closedCaptioning?.srt,
  );

  const adConfigurator = useAdConfigurator({
    publisherName,
    isEmbedded: useEmbeddedAds,
    isLiveVideo: isLive,
    gppStatus,
    allowAdSkip: true,
  });

  const [canDisplayMuteBlock, setCanDisplayMuteBlock] = useState(true);
  const analyticsMetadata = useMemo(
    () => ({
      ...video,
      autoPlay,
      continuousPlay,
      nowPlayingInitiator: playReason,
      videoexperience: 'vertical',
      videoitemnumber: playlistIndex,
      videoplaylistitem: playlistId,
      videoinitiate: playReason,
    }),
    [video, autoPlay, continuousPlay, playReason, playlistIndex, playlistId],
  );

  // Temporary fix for a browser issue with safari that if a user mutes via our mute block will lock the player in a nonrecoverable state
  useEffect(() => {
    if (BrowserDetection.isSafari() && BrowserDetection.version() === 16) {
      setCanDisplayMuteBlock(false);
    } else {
      setCanDisplayMuteBlock(true);
    }
  }, []);

  const [hasError, setHasError] = useState(false);

  const onNewPlaylistItem = useCallback(() => {
    setHasError(false);
  }, []);

  const onGammaError = useCallback((/** @type {Error} */ error) => {
    const jsErr = new Error('Gamma Error from JWPlayer');
    logError(jsErr, error);
    setHasError(true);
    // eslint-disable-next-line no-console
    console.error('Gamma Error', error);
  }, []);

  // Useful for scanning all JWPlayerEvents. Gamma prop is `onAll`
  // const onGammaEvent = useCallback((event, context) => {
  //   // eslint-disable-next-line no-console
  //   console.log('(g.vp.i153.x) Gamma Event', event, context);
  // }, []);

  const videoControl = useVideoControl({
    autoPlay: true,
    relayEvents: {
      /**
       *
       * @param {boolean} isPaused
       */
      onPauseChange: (/** @type {boolean} */ isPaused) => {
        if (!isPaused) onVideoPlay();
      },
      /**
       * Fires on the first frame of video playback
       */
      onFirstFrame: () => {
        playCount.current += 1;
        handleVideoStart();
      },
      /**
       * Fires when an ad is clicked. Ads that don't have a click through link
       * need a manual override to continue the ad playback.
       */
      onAdClick: () => {
        if (videoControl.state.isPaused) {
          videoControl.state.onPauseChange(false);
        }
      },
      /**
       * Fires when the ad is finished, either played to completion or skipped.
       * Triggers the ad played handler and well as ensures that if the ad was paused before
       * skipping, the video resumes playback.
       */
      onAdBreakEnd() {
        handeAdPlayed();
        if (videoControl.state.isPaused) {
          videoControl.state.onPauseChange(false);
        }
      },
      onVideoEnd,
      onPlaylistItem: onNewPlaylistItem,
      onError: onGammaError,
      // onAll: onGammaEvent, // useful for devs, see comment above
    },
  });

  useEffect(() => {
    if (videoControl.state.isPaused) {
      videoControl.state.onPauseChange(false);
    }
  }, [video?.id]);

  useEffect(() => {
    if (playCount.current > 1) {
      const newPlayReason = videoControl.state.playReason === 'repeat' ? 'continuous' : 'manual';
      setPlayReason(newPlayReason);
    }
  }, [videoControl.state.playReason]);

  const handleCaptionToggle = useCallback(() => {
    if (!jwRef.current) return;

    window.PLAYER_DEBUG = jwRef.current;

    const currentCaptionState = jwRef.current.getCurrentCaptions();
    const captionStateEnabled = currentCaptionState === 1;
    const nextCaptionState = captionStateEnabled ? 0 : 1; // Toggle between 0 (off) and 1 (on)
    jwRef.current.setCurrentCaptions(nextCaptionState);

    // Apply caption styles
    // - JWPlayer requires styles to be set each time captions are toggled
    // - Captions must be enabled for styles to apply
    jwRef.current.setCaptions({
      color: '#ffffff', // White text for good contrast
      fontSize: 5,
      fontFamily: 'Arial, sans-serif', // Clean, readable font
      backgroundColor: '#000000', // Black background for contrast
      backgroundOpacity: 75, // Semi-transparent background; needs to be an integer 0-100
      fontWeight: 'normal', // Standard weight
      textAlign: 'center', // Centered text
    });

    // Update local state
    videoControl.state.onCaptionsChange(nextCaptionState === 1);
  }, [videoControl.state]);

  // Initialize captions to off state when video loads
  useEffect(() => {
    if (jwRef.current && hasCaptions) {
      jwRef.current.setCurrentCaptions(0); // Ensure captions start OFF
      videoControl.state.onCaptionsChange(false); // Sync UI state
    }
  }, [video?.id]);

  return (
    <GridStack
      onClick={/** @param {React.MouseEvent<HTMLDivElement>} e */ (e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onKeyDown={/** @param {React.KeyboardEvent<HTMLDivElement>} e */ (e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          e.preventDefault();
        }
        if (e.key === 'Escape') {
          e.stopPropagation();
          e.preventDefault();
        }
      }}
      tabIndex={-1}
      ref={primaryFocusElementRef}
      data-testid={autoPlay ? 'gamma-video-player-autoplay' : 'gamma-video-player'}
      className={classNames(`${styles.verticalVideoContainer} video-container`, {
        paused: videoControl.state.isPaused,
        started: videoControl.state.hasPlaybackStarted,
      })}
    >
      <Head>
        <link
          key="jw-player-library-preload"
          rel="preload"
          as="script"
          href={JW_PLAYER_LIBRARY_URL}
        />
      </Head>
      <Gamma
        key={`gamma-vertical-video-${playAd ? 'ad-on' : 'ad-off'}`}
        jwPlayerRef={jwRef}
        jwPlayerLibraryUrl={JW_PLAYER_LIBRARY_URL}
        jwPlayerLibraryKey={JW_PLAYER_LIBRARY_KEY}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...videoControl.state}
        $t={$t}
        $tMetadata={analyticsMetadata}
        config={config}
        playlistItem={playlistItem}
        advertising={playAd ? adConfigurator : undefined}
      />
      <Slate
        isPaused={videoControl.state.isPaused}
        isExpired={isExpired}
        isHidden={hasError}
        hasPlaybackStarted={videoControl.state.hasPlaybackStarted}
      />
      <div className={`info-layer ${styles.infoLayer}`}>
        <ControlOverlay
          clickUnmute={videoControl.state.onMuteChange}
          isTouchSupported={Boolean(isTouchSupported)}
          title={title}
          videoState={videoControl.state}
          onPauseChange={videoControl.state.onPauseChange}
          handlePrevious={handlePrevious}
          canDisplayMuteBlock={canDisplayMuteBlock}
          hasError={hasError}
          hasCaptions={hasCaptions}
          onCaptionToggle={handleCaptionToggle}
          handleNext={() => {
            handleNext();
            if (videoControl.state.isPaused) {
              videoControl.state.onPauseChange(false);
            }
          }}
          handleClose={handleClose}
        />
      </div>
    </GridStack>
  );
}
